import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
class FlatFee extends Base implements EntityType {
    flatFeeId: uuid;
    name: string;
    amount: number;
    description?: string | null;
    isDefault: boolean;
    completionDate?: Date | null;
    earned: boolean;
    invoiceId?: uuid | null;
    relatedTo?: uuid | null;
    earnedThisPeriod?: number | null;
    earnedAfterThisPeriod: boolean;
    position?: number | null;
    constructor(doc: typeof FlatFee | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            flatFeeId: genUUID(),
            name: null,
            amount: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "flatFee";
        // @ts-ignore
        this.flatFeeId = doc._flatFeeId || doc.flatFeeId || doc.flatfeeid || genUUID();
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.amount = util.coalesce(util.parseFloat(doc._amount), util.parseFloat(doc.amount), util.parseFloat(doc.amount), null);
        // @ts-ignore
        this.description = doc._description || doc.description || null;
        // @ts-ignore
        this.isDefault = util.defaultBoolean([doc.isDefault, doc._isDefault, doc.isdefault, false]);
        // @ts-ignore
        this.completionDate = DateUtil.timestampOnly(doc._completionDate) || DateUtil.timestampOnly(doc.completionDate) || DateUtil.timestampOnly(doc.completiondate) || null;
        // @ts-ignore
        this.earned = util.defaultBoolean([doc.earned, doc._earned, doc.earned, false]);
        // @ts-ignore
        this.invoiceId = doc._invoiceId || doc.invoiceId || doc.invoiceid || null;
        // @ts-ignore
        this.relatedTo = doc._relatedTo || doc.relatedTo || doc.relatedto || null;
        // @ts-ignore
        this.earnedThisPeriod = util.coalesce(util.parseFloat(doc._earnedThisPeriod), util.parseFloat(doc.earnedThisPeriod), util.parseFloat(doc.earnedthisperiod), null);
        // @ts-ignore
        this.earnedAfterThisPeriod = util.defaultBoolean([doc.earnedAfterThisPeriod, doc._earnedAfterThisPeriod, doc.earnedafterthisperiod, false]);
        // @ts-ignore
        this.position = util.coalesce(util.parseInt(doc._position), util.parseInt(doc.position), util.parseInt(doc.position), null);
    }
    get id(): uuid { return this.flatFeeId; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this flat fee"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.flatFeeId; }
    static override getNonRefArrayFields(): string[] { return []; }
    static override getDefaultEntries(): FlatFee[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'flatFee',
    table: 'FLAT_FEE',
    comment: 'A flat fee is a fixed-price service block attached to a matter or case type template. Each record represents one named, priced service (e.g. "Divorce Filing") that the attorney marks as earned when the work is complete, at which point it becomes billable and is included in the next invoice. A flat fee may optionally be broken into child milestones (flatFeeMilestone) that allow partial earning before the whole fee is considered done.',
    fields: {
        flatFeeId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this flat fee'
        },

        /**
         * The name of this flat fee bucket (used to be called 'fee')
         */
        name: {type: 'string', required: true, comment: 'The name of the flat fee bucket'},

        /**
         * The amount of money allocated to this bucket
         */
        amount: {type: 'money', required: true, comment: 'The amount of money allocated to this bucket'},

        /**
         * A description of the service provided by this bucket
         */
        description: {type: 'string', comment: 'A description of the service provided by this bucket'},

        /**
         * If there are multiple flat fees in a case with ONLY flat fees, mark this fee as the default.
         * This fee will then be used as the default for all new billable tasks.
         */
        isDefault: {type: 'boolean', default: false, comment: 'Default flat fee to use for new tasks'},

        /**
         * When was this milestone completed
         */
        completionDate: {type: 'timestamp', comment: 'Date and time when the milestone was completed'},

        /**
         * Flat fee buckets are often viewed as milestones, and the attorney can then charge the client
         * for that bucket.
         */
        earned: {type: 'boolean', comment: 'Do we consider this milestone as earned?'},

        /**
         * Which invoice this milestone is billed in.
         */
        invoiceId: {type: 'uuid', entityType: 'invoice', comment: 'The invoice this flat fee is billed in'},

        /**
         * An optional contact/business this is related to
         */
        relatedTo: {type: 'uuid', entityType: 'contact', comment: 'An optional contact/business this is related to'},

        /**
         * Used in intermediate calculations
         */
        earnedThisPeriod: {type: 'money', transient: true, comment: 'Amount earned this (invoice) period'},

        /**
         * Used in intermediate calculations
         */
        earnedAfterThisPeriod: {type: 'boolean', transient: true, comment: 'Was this flat fee earned after the current period?'},

        /**
         * Positions for flat fees
         */
        position: {
            type: 'int',
            comment: 'A position (order) for the flatFee (within a matter)'
        }
    },
    indexes: [
        {name: 'notDeleted', fn: t => t.status !== 'D'}
    ],
    maps: [
        {name: 'invoiceId', keysFn: t => t.invoiceId, presentFn: (t, k) => t.invoiceId === k},
        {name: 'matterId', keysFn: t => t.entityType === 'matter' ? t.entityId : null, presentFn: (t, k) => t.entityId === k},
        {name: 'caseTypeId', keysFn: t => t.entityType === 'caseType' ? t.entityId : null, presentFn: (t, k) => t.entityId === k},
        {name: 'relatedTo', keysFn: t => t.relatedTo, presentFn: (t, k) => t.relatedTo === k}
    ],
});
    }
}
export default FlatFee;
